import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Bookmark, BookmarkCheck, Sun, Moon, Copy, Sparkles, Globe, Volume2, Pause, Play, Square, Loader2, Languages } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SpokenText, { parseSpoken } from "@/components/SpokenText";
import { GLOSSARY, LANGUAGES } from "@/data/glossary";

function stripMd(t) {
  return (t || "").replace(/^#{1,6}\s+/gm, "").replace(/\*\*/g, "").replace(/>\s/g, "");
}

export default function Reader() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [paper, setPaper] = useState(false);
  const [marked, setMarked] = useState(false);
  const [lang, setLang] = useState("original");
  const [translated, setTranslated] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [speakState, setSpeakState] = useState("idle"); // idle | playing | paused
  const [activeSentence, setActiveSentence] = useState(-1);
  const [focusIndex, setFocusIndex] = useState(-1);
  const [params] = useSearchParams();
  const idxRef = useRef(0);

  const body = (lang !== "original" && translated) ? translated : data?.document.body;
  const spoken = useMemo(() => parseSpoken(body || ""), [body]);

  useEffect(() => {
    setData(null); setLang("original"); setTranslated(null); setShowTerms(false);
    stopSpeak();
    api.get(`/documents/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load this section."));
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (user && user.bookmarks) setMarked(user.bookmarks.includes(id));
    else setMarked(false);
  }, [user, id]);

  useEffect(() => () => stopSpeak(), []);

  // citation deep-link: focus a matching sentence
  useEffect(() => {
    const focus = params.get("focus");
    if (!focus || !data || lang !== "original") { return; }
    const norm = (s) => s.toLowerCase().replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
    const target = norm(focus);
    let best = -1;
    for (const s of spoken.sentences.map((t, i) => ({ t: norm(t), i }))) {
      if (s.t.length > 12 && (target.includes(s.t.slice(0, 40)) || s.t.includes(target.slice(0, 40)))) { best = s.i; break; }
    }
    if (best >= 0) {
      setFocusIndex(best);
      setTimeout(() => document.querySelector(`[data-sentence="${best}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" }), 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, lang]);

  const terms = useMemo(() => {
    if (!data) return [];
    const low = data.document.body.toLowerCase();
    return Object.keys(GLOSSARY).filter((t) => new RegExp(`\\b${t}\\b`).test(low)).map((t) => ({ term: t, ...GLOSSARY[t] }));
  }, [data]);

  const toggleBookmark = useCallback(async () => {
    if (!user) { toast("Sign in to save bookmarks"); return navigate("/login"); }
    try {
      if (marked) { await api.delete(`/bookmarks/${id}`); setMarked(false); toast("Bookmark removed"); }
      else { await api.post("/bookmarks", { document_id: id }); setMarked(true); toast.success("Bookmarked"); }
    } catch { toast.error("Could not update bookmark"); }
  }, [user, marked, id, navigate]);

  const changeLang = async (code) => {
    stopSpeak();
    setLang(code);
    if (code === "original") { setTranslated(null); return; }
    setTranslating(true);
    try {
      const { data: res } = await api.post("/translate", { doc_id: id, text: data.document.body, target_lang: code });
      setTranslated(res.translation);
    } catch { toast.error("Translation failed. Please try again."); setLang("original"); }
    finally { setTranslating(false); }
  };

  // ---- browser speech synthesis (sentence-by-sentence with highlight) ----
  function stopSpeak() {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    idxRef.current = 0;
    setActiveSentence(-1);
    setSpeakState("idle");
  }
  const speakFrom = (sentences, start) => {
    if (start >= sentences.length) { setActiveSentence(-1); setSpeakState("idle"); return; }
    idxRef.current = start;
    setActiveSentence(start);
    setFocusIndex(-1);
    const u = new SpeechSynthesisUtterance(sentences[start]);
    const v = LANGUAGES.find((l) => l.code === lang);
    u.lang = v ? v.voice : "en-US";
    u.rate = 0.98;
    u.onend = () => {
      if (idxRef.current !== start) return; // cancelled / restarted
      speakFrom(sentences, start + 1);
    };
    window.speechSynthesis.speak(u);
  };
  const startSpeak = () => {
    if (!window.speechSynthesis) { toast.error("Your browser does not support speech."); return; }
    window.speechSynthesis.cancel();
    const sentences = spoken.sentences.length ? spoken.sentences : [stripMd(body)];
    setSpeakState("playing");
    speakFrom(sentences, 0);
  };
  const toggleSpeak = () => {
    const synth = window.speechSynthesis;
    if (speakState === "idle") startSpeak();
    else if (speakState === "playing") { synth.pause(); setSpeakState("paused"); }
    else { synth.resume(); setSpeakState("playing"); }
  };

  if (!data) return <div className="pt-40 text-center text-slate-400" data-testid="reader-loading">Opening the text…</div>;
  const { document: doc, work, prev, next } = data;

  const copyText = () => {
    navigator.clipboard.writeText(`${doc.title}\n\n${body}\n\n— ${work.title}, INDIC (Mahakali Tribunal)`);
    toast.success("Passage copied");
  };

  const btn = (on) => `p-2 rounded-sm ${on ? "text-indic-gold" : paper ? "text-indic-crimson/70 hover:bg-black/5" : "text-slate-400 hover:text-indic-gold hover:bg-white/5"}`;

  return (
    <div className={`pt-16 lg:pt-[72px] min-h-screen ${paper ? "manuscript grain" : "bg-indic-indigo"}`} data-testid="reader-page">
      <div className={`sticky top-16 lg:top-[72px] z-30 border-b ${paper ? "bg-[#F8F4EB]/95 border-indic-paper-border" : "bg-[#0B1021]/95 border-indic-card-border"} backdrop-blur`}>
        <div className="mx-auto max-w-3xl px-4 sm:px-6 flex items-center justify-between h-12 gap-2">
          <Link to={`/library/${work.slug}`} className={`inline-flex items-center gap-1.5 text-sm shrink-0 ${paper ? "text-indic-crimson" : "text-slate-400 hover:text-indic-gold"}`} data-testid="reader-back">
            <ArrowLeft size={15} /> <span className="hidden sm:inline">{work.title}</span><span className="sm:hidden">Back</span>
          </Link>
          <div className="flex items-center gap-1">
            {/* language */}
            <div className="relative flex items-center">
              <Globe size={15} className={paper ? "text-indic-crimson/60 ml-1" : "text-slate-500 ml-1"} />
              <select value={lang} onChange={(e) => changeLang(e.target.value)} data-testid="reader-language"
                className={`bg-transparent text-xs font-sans py-1 pl-1 pr-1 focus:outline-none cursor-pointer ${paper ? "text-indic-crimson" : "text-slate-300"}`}>
                {LANGUAGES.map((l) => <option key={l.code} value={l.code} className="bg-indic-card text-slate-200">{l.label}</option>)}
              </select>
              {translating && <Loader2 size={14} className="animate-spin text-indic-gold" />}
            </div>
            <button onClick={toggleSpeak} className={btn(speakState !== "idle")} title="Listen" data-testid="reader-listen">
              {speakState === "playing" ? <Pause size={17} /> : speakState === "paused" ? <Play size={17} /> : <Volume2 size={17} />}
            </button>
            {speakState !== "idle" && <button onClick={stopSpeak} className={btn(false)} title="Stop" data-testid="reader-stop"><Square size={15} /></button>}
            {terms.length > 0 && <button onClick={() => setShowTerms((s) => !s)} className={btn(showTerms)} title="Sanskrit terms" data-testid="reader-terms-toggle"><Languages size={17} /></button>}
            <button onClick={copyText} className={btn(false)} title="Copy" data-testid="reader-copy"><Copy size={16} /></button>
            <button onClick={() => setPaper((p) => !p)} className={btn(false)} title="Reading mode" data-testid="reader-mode-toggle">{paper ? <Moon size={16} /> : <Sun size={16} />}</button>
            <button onClick={toggleBookmark} className={btn(marked)} title="Bookmark" data-testid="reader-bookmark">{marked ? <BookmarkCheck size={18} /> : <Bookmark size={16} />}</button>
          </div>
        </div>
      </div>

      <article className="mx-auto max-w-3xl px-4 sm:px-6 py-14 lg:py-20">
        <div className={`text-xs font-sans uppercase tracking-[0.25em] ${paper ? "text-indic-crimson/70" : "text-indic-gold/80"}`}>
          {doc.group_title}{doc.code ? ` · ${doc.code}` : ""}
        </div>
        <h1 className={`mt-4 font-serif text-4xl lg:text-5xl font-semibold leading-tight ${paper ? "text-[#7A1326]" : "text-amber-50"}`} data-testid="reader-title">{doc.title}</h1>
        <div className={`mt-3 text-sm ${paper ? "text-[#6b5d44]" : "text-slate-500"}`}>
          {work.title} · {doc.word_count} words{lang !== "original" && translated ? ` · translated to ${lang}` : ""}
        </div>
        <div className={`mt-4 h-px w-24 ${paper ? "bg-indic-crimson/40" : "bg-indic-gold/40"}`} />

        {showTerms && terms.length > 0 && (
          <div className={`mt-8 rounded-sm border p-5 ${paper ? "border-indic-paper-border bg-black/[0.02]" : "border-indic-gold/20 bg-indic-card"}`} data-testid="reader-terms-panel">
            <div className={`text-xs font-sans uppercase tracking-wider mb-3 ${paper ? "text-indic-crimson/70" : "text-indic-gold/80"}`}>Sanskrit terms in this passage</div>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {terms.map((t) => (
                <div key={t.term} className="flex gap-2">
                  <span className="font-deva text-lg text-indic-gold shrink-0">{t.script}</span>
                  <div>
                    <dt className={`font-serif capitalize ${paper ? "text-[#2A2218]" : "text-amber-50"}`}>{t.term}</dt>
                    <dd className={`text-sm ${paper ? "text-[#6b5d44]" : "text-slate-400"}`}>{t.def}</dd>
                  </div>
                </div>
              ))}
            </dl>
          </div>
        )}

        <div className={`mt-10 text-lg leading-loose ${paper ? "text-[#2A2218] font-serif" : "text-slate-200/90"}`} data-testid="reader-body">
          {translating ? <div className="flex items-center gap-2 text-slate-400"><Loader2 size={18} className="animate-spin" /> Translating with INDIC Scholar…</div> : <SpokenText blocks={spoken.blocks} active={activeSentence} focus={focusIndex} />}
        </div>

        <div className="mt-16 grid grid-cols-2 gap-4">
          {prev ? (
            <Link to={`/read/${prev.id}`} className={`group rounded-sm border p-4 ${paper ? "border-indic-paper-border hover:border-indic-crimson/40" : "border-indic-card-border hover:border-indic-gold/40"}`} data-testid="reader-prev">
              <span className={`flex items-center gap-1 text-xs uppercase tracking-wider ${paper ? "text-indic-crimson/60" : "text-slate-500"}`}><ArrowLeft size={13} /> Previous</span>
              <span className={`mt-1 block font-serif text-[15px] ${paper ? "text-[#2A2218]" : "text-amber-50"}`}>{prev.title}</span>
            </Link>
          ) : <div />}
          {next ? (
            <Link to={`/read/${next.id}`} className={`group rounded-sm border p-4 text-right ${paper ? "border-indic-paper-border hover:border-indic-crimson/40" : "border-indic-card-border hover:border-indic-gold/40"}`} data-testid="reader-next">
              <span className={`flex items-center justify-end gap-1 text-xs uppercase tracking-wider ${paper ? "text-indic-crimson/60" : "text-slate-500"}`}>Next <ArrowRight size={13} /></span>
              <span className={`mt-1 block font-serif text-[15px] ${paper ? "text-[#2A2218]" : "text-amber-50"}`}>{next.title}</span>
            </Link>
          ) : <div />}
        </div>

        <div className={`mt-10 rounded-sm border p-5 flex items-center justify-between gap-4 ${paper ? "border-indic-paper-border bg-black/[0.02]" : "border-indic-card-border bg-indic-card"}`}>
          <p className={`text-sm ${paper ? "text-[#6b5d44]" : "text-slate-400"}`}>Have a question about this passage?</p>
          <Link to="/scholar" className="inline-flex items-center gap-2 text-sm font-semibold text-indic-gold hover:text-indic-gold-soft" data-testid="reader-ask-scholar"><Sparkles size={15} /> Ask INDIC Scholar</Link>
        </div>
      </article>
    </div>
  );
}
